import React, { useState, useEffect } from 'react';
import { useLeads } from '../hooks/useLeads';
import { LeadList } from '../components/leads/LeadList';
import { LeadStats } from '../components/leads/LeadStats';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useChatbot } from '../contexts/ChatbotContext';
import { ChevronLeft, Bot, BarChart2, X, UserPlus } from 'lucide-react';

export default function Leads() {
  const { leads, loading, error, toggleLeadStatus, deleteLeads } = useLeads();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { chatbotPublicId } = useChatbot();
  const [chatbotInfo, setChatbotInfo] = useState<{ name: string, id: string } | null>(null);
  const [showStatsMobile, setShowStatsMobile] = useState(false);
  
  const urlChatbotId = searchParams.get('chatbot');
  const isFilteredView = urlChatbotId && urlChatbotId !== chatbotPublicId;
  
  // Fetch chatbot details if viewing a different chatbot than the default
  useEffect(() => {
    if (urlChatbotId) {
      const fetchChatbotInfo = async () => {
        try {
          const { data, error } = await supabase
            .from('chatbots')
            .select('name')
            .eq('public_id', urlChatbotId)
            .single();
            
          if (error) throw error;
          
          if (data) {
            setChatbotInfo({ name: data.name, id: urlChatbotId });
          }
        } catch (err) {
          console.error('Error fetching chatbot info:', err);
        }
      };
      
      fetchChatbotInfo();
    }
  }, [urlChatbotId]);
  
  // Handle returning to default chatbot
  const handleReturnToDefault = () => {
    navigate('/app/visitors');
  };

  // Toggle stats on mobile
  const toggleStatsMobile = () => {
    setShowStatsMobile(prev => !prev);
  };

  return (
    <div className="p-4 md:p-8 bg-gradient-to-b from-gray-900 to-black min-h-screen">
      <div className="mb-6 md:mb-8 pb-4 border-b border-white/10">
        <div className="flex items-center gap-2 animate-fade-in">
          <div className="flex items-center gap-2 bg-gradient-to-r from-violet-600/20 to-cyan-500/20 p-2 rounded-full mr-3 text-white hidden md:flex">
            <UserPlus className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-white mb-2 group">
              Collected leads
              <span className="inline-block ml-2 bg-gradient-to-r from-violet-600/20 to-cyan-500/20 text-white text-sm px-2.5 py-0.5 rounded-full">
                {leads.length}
              </span>
            </h1>
            <p className="text-gray-400 text-sm md:text-base">Contacts detected during conversations with the chatbot</p>
          </div>
          
          {/* Mobile stats toggle button */}
          <div className="flex-1 flex justify-end md:hidden">
            <button 
              onClick={toggleStatsMobile}
              className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-violet-600/40 to-cyan-500/40 text-white text-sm rounded-lg shadow-sm"
            >
              {showStatsMobile ? <X className="w-4 h-4" /> : <BarChart2 className="w-4 h-4" />}
              <span className="hidden sm:inline">{showStatsMobile ? 'Close Stats' : 'View Stats'}</span>
            </button>
          </div>
        </div>
        
        {isFilteredView && chatbotInfo && (
          <div className="mt-4 p-3 bg-gradient-to-r from-violet-900/20 to-cyan-900/20 rounded-lg flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-2">
              <Bot className="text-white w-5 h-5 flex-shrink-0" />
              <span className="text-white text-sm md:text-base">
                Viewing leads from <span className="font-semibold">{chatbotInfo.name}</span>
              </span>
            </div>
            <button 
              onClick={handleReturnToDefault} 
              className="flex items-center justify-center gap-1 px-3 py-1.5 bg-gradient-to-r from-violet-600/40 to-cyan-500/40 text-white text-sm rounded-md shadow-sm"
            >
              <ChevronLeft className="w-4 h-4" />
              Return to default chatbot
            </button>
          </div>
        )}
      </div>

      {/* Mobile stats panel that slides in from the right */}
      <div className={`fixed inset-y-0 right-0 w-[85%] max-w-md z-40 bg-gray-900/95 backdrop-blur-lg border-l border-white/10 p-4 shadow-xl transform transition-transform duration-300 ease-in-out ${showStatsMobile ? 'translate-x-0' : 'translate-x-full'} md:hidden overflow-y-auto`}>
        <div className="flex justify-between items-center mb-4 pb-3 border-b border-white/10">
          <h2 className="text-xl font-bold text-white">Lead Statistics</h2>
          <button onClick={toggleStatsMobile} className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
        <LeadStats leads={leads} />
      </div>

      {/* Dark overlay when stats are shown on mobile */}
      {showStatsMobile && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 md:hidden"
          onClick={toggleStatsMobile}
        ></div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 md:gap-8">
        <div className="lg:col-span-3 animate-fade-in">
          <LeadList 
            leads={leads} 
            loading={loading} 
            error={error}
            onToggleStatus={toggleLeadStatus}
            onDeleteLeads={deleteLeads}
          />
        </div>
        <div className="hidden lg:block lg:col-span-1">
          <LeadStats leads={leads} />
        </div>
      </div>
    </div>
  );
}